/* eslint-disable react/no-multi-comp */
import { forwardRef, useEffect, useRef } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

//hooks
import { useDatepicker } from '@/hooks/datepicker/useDatepicker';
import { UPDATE_MIN_DATE, UPDATE_PERIOD } from '@/utils/hooks/datepicker/actions';
import useDateLimits from '@/utils/hooks/datepicker/useDateLimits';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

//Lib
import datepickerDateFormat from '@/lib/datepicker/dateFormat';
import { PREVIOUS_MONTH } from '@/lib/periods';
import { FACEBOOK } from '@/lib/socialProfile';

//components
import Datepicker from '@/components/base/Datepicker';
import DatepickerInput from '@/components/base/Datepicker/DatepickerInput';

dayjs.extend(utc);
dayjs.extend(timezone);

const DatepickerReportsContent = forwardRef(
    (
        { toggleDatepicker, setReferenceElement, isLeftIcon, placeholder, minDate, isDisabled },
        ref
    ) => {
        const { dispatch, datepickerData } = useDatepicker();

        useEffect(() => {
            // if selected date is before the new min date, update the period to default
            if (dayjs(datepickerData.date?.[0]).isBefore(minDate)) {
                dispatch({ type: UPDATE_PERIOD, payload: PREVIOUS_MONTH });
            }
            dispatch({
                type: UPDATE_MIN_DATE,
                payload: minDate
            });
        }, [minDate]);

        return (
            <DatepickerInput
                ref={ref}
                placeholder={placeholder}
                isLeftIcon={isLeftIcon}
                noBorder={false}
                setReferenceElement={setReferenceElement}
                isDisabled={isDisabled}
                toggleDatepicker={toggleDatepicker}
            />
        );
    }
);

DatepickerReportsContent.displayName = 'DatepickerReportsContent';

const DatepickerReports = ({
    isDisabled = false,
    periodSelected,
    setPeriodSelected,
    periods = 'exports',
    selectedOptions = [],
    ...restProps
}) => {
    const { t } = useTranslation();
    const { maxDate, getMaxDate, getMinDate, getDataRetentionDate } = useDateLimits();
    const { user } = useUser();
    const inputRef = useRef(null);

    // get all max date from all selected options and sort descending
    const newMaxDate = selectedOptions
        ?.map((profile) => getMaxDate(profile))
        ?.sort((a, b) => a.localeCompare(b))
        ?.reverse();

    const newMinDate = selectedOptions
        ?.map((profile) => getMinDate(profile))
        ?.sort((a, b) => a.localeCompare(b))
        ?.reverse();

    // get the min date based on data retention
    const datePickerMinDate = newMinDate[0] ?? getDataRetentionDate();

    return (
        <>
            <Datepicker
                date={periodSelected.date ?? null}
                periods={periods}
                periodSelected={periodSelected.period ?? PREVIOUS_MONTH}
                timezone={user?.settings?.timezone}
                options={{
                    placement: 'bottom-start',
                    dateFormat: datepickerDateFormat[user?.settings?.date_format] ?? 'Y/m/d',
                    mode: 'range',
                    parseDate: (dateStr) => {
                        return dayjs(dateStr).toDate();
                    },
                    maxDate: !selectedOptions?.length ? maxDate : newMaxDate[0],
                    minDate: datePickerMinDate
                }}
                onChange={(data) => {
                    if (data.date && data.date.length === 2) {
                        setPeriodSelected(data);
                    }
                }}
                className="!z-100"
                {...restProps}>
                <DatepickerReportsContent
                    minDate={datePickerMinDate}
                    ref={inputRef}
                    isDisabled={isDisabled}
                />
            </Datepicker>
            {selectedOptions?.length &&
            selectedOptions?.filter((el) => el.social_network_type === FACEBOOK)?.length ? (
                <div className="mt-2 font-GMedium text-sm font-light text-icoGray-500">
                    {t('commons:explanation_period')}
                </div>
            ) : null}
        </>
    );
};

DatepickerReports.propTypes = {
    periodSelected: PropTypes.object,
    setPeriodSelected: PropTypes.func,
    periods: PropTypes.string,
    selectedOptions: PropTypes.array,
    newMinDate: PropTypes.string
};

export default DatepickerReports;
